import { useEffect, useState, useMemo } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, FileDown } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";
import { exportToExcel } from "@/lib/excel";
import {
  AreaChart, Area, ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";

export default function Loans() {
  const pinPrompt = usePinPrompt();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ lender: "", principal: 0, rate: 0, tenure_months: 12 });
  const [schedule, setSchedule] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => setRows((await api.get("/loans")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.lender || !f.principal || !f.tenure_months) { toast.error("Fill required fields"); return; }
    await pinPrompt();
    try { await api.post("/loans", f); toast.success("Loan added"); setOpen(false); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await pinPrompt();
    try { await api.delete(`/loans/${id}`); toast.success("Deleted"); setSchedule(null); setSelected(null); load(); } catch (e) { toast.error("Failed"); }
  };
  const openSchedule = async (id) => {
    setSelected(id);
    const { data } = await api.get(`/loans/${id}/schedule`);
    setSchedule(data);
  };
  const payEMI = async (id) => {
    await pinPrompt();
    try { await api.post(`/loans/${id}/pay-installment`); toast.success("EMI paid"); load(); openSchedule(id); }
    catch (e) { toast.error("Failed"); }
  };

  // Derived stats for chart & summary
  const stats = useMemo(() => {
    if (!schedule?.schedule) return null;
    const items = schedule.schedule;
    const totalInterest = items.reduce((s, r) => s + (r.interest || 0), 0);
    const totalPaid = (schedule.loan?.emi || 0) * items.length;
    const paidSoFar = (schedule.loan?.emi || 0) * (schedule.loan?.paid_installments || 0);
    return {
      totalInterest: Math.round(totalInterest),
      totalPayout: Math.round(totalPaid),
      paidSoFar: Math.round(paidSoFar),
      remaining: Math.round(totalPaid - paidSoFar),
    };
  }, [schedule]);

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <div className="overline">Financing</div>
          <h1 className="text-3xl font-bold tracking-tight">Loan Accounts</h1>
        </div>
        <Button className="btn-primary" onClick={() => setOpen(true)} data-testid="loan-add"><Plus size={14} /> Add Loan</Button>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-lg overflow-x-auto">
          <table className="acc-table w-full">
            <thead><tr><th>Lender</th><th className="num">Principal</th><th className="num">Rate</th><th className="num">EMI</th><th className="num">Outstanding</th><th>Paid</th><th></th></tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l._id_str} className={selected === l._id_str ? "bg-emerald-50" : ""} data-testid={`loan-row-${l._id_str}`}>
                  <td><button className="font-semibold text-[#064E3B] hover:underline" onClick={() => openSchedule(l._id_str)} data-testid={`loan-schedule-btn-${l._id_str}`}>{l.lender}</button></td>
                  <td className="num">{fmtINR(l.principal)}</td>
                  <td className="num">{l.rate}%</td>
                  <td className="num">{fmtINR(l.emi)}</td>
                  <td className="num">{fmtINR(l.outstanding)}</td>
                  <td className="num">{l.paid_installments}/{l.tenure_months}</td>
                  <td><button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => del(l._id_str)}><Trash2 size={14} /></button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No loans.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-3 bg-white border border-border rounded-lg p-4" data-testid="loan-schedule-panel">
          {!schedule && <div className="text-sm text-muted-foreground p-6 text-center">Click a lender name to see the amortisation schedule & payoff chart.</div>}
          {schedule && (
            <>
              <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="overline">Amortisation Schedule</div>
                  <div className="font-bold text-lg">{schedule.loan.lender}</div>
                  <div className="text-xs text-muted-foreground">
                    {schedule.loan.tenure_months} months · EMI {fmtINR(schedule.loan.emi)} · Rate {schedule.loan.rate}%
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => exportToExcel(schedule.schedule, `${schedule.loan.lender}-schedule.xlsx`)} data-testid="loan-schedule-excel"><FileDown size={14} /> Excel</Button>
                  <Button className="btn-primary" onClick={() => payEMI(schedule.loan._id_str)} data-testid="loan-pay-emi">Pay next EMI</Button>
                </div>
              </div>

              {/* Summary chips */}
              {stats && (
                <div className="grid grid-cols-4 gap-2 mb-4 text-xs">
                  <div className="border border-border rounded p-2">
                    <div className="overline text-[10px]">Total Payout</div>
                    <div className="font-mono font-bold text-slate-800">{fmtINR(stats.totalPayout)}</div>
                  </div>
                  <div className="border border-border rounded p-2">
                    <div className="overline text-[10px]">Total Interest</div>
                    <div className="font-mono font-bold text-[#991B1B]">{fmtINR(stats.totalInterest)}</div>
                  </div>
                  <div className="border border-border rounded p-2">
                    <div className="overline text-[10px]">Paid So Far</div>
                    <div className="font-mono font-bold text-[#166534]">{fmtINR(stats.paidSoFar)}</div>
                  </div>
                  <div className="border border-border rounded p-2">
                    <div className="overline text-[10px]">Remaining</div>
                    <div className="font-mono font-bold text-[#064E3B]">{fmtINR(stats.remaining)}</div>
                  </div>
                </div>
              )}

              {/* Payoff chart */}
              <div className="border border-border rounded-md mb-4" style={{ height: 240 }} data-testid="loan-payoff-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={schedule.schedule} margin={{ top: 10, right: 20, left: 10, bottom: 8 }}>
                    <defs>
                      <linearGradient id="balColor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#064E3B" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#064E3B" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="principal" name="Principal" stackId="p" fill="#166534" />
                    <Bar dataKey="interest" name="Interest" stackId="p" fill="#B45309" />
                    <Area type="monotone" dataKey="balance" name="Outstanding" stroke="#064E3B" fill="url(#balColor)" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="max-h-72 overflow-y-auto">
                <table className="acc-table w-full">
                  <thead><tr><th className="num">#</th><th className="num">EMI</th><th className="num">Interest</th><th className="num">Principal</th><th className="num">Balance</th></tr></thead>
                  <tbody>
                    {schedule.schedule.map((s) => (
                      <tr key={s.month} className={s.month <= schedule.loan.paid_installments ? "bg-emerald-50" : ""} data-testid={`loan-schedule-row-${s.month}`}>
                        <td className="num">{s.month}</td>
                        <td className="num">{fmtINR(s.emi)}</td>
                        <td className="num text-[#B45309]">{fmtINR(s.interest)}</td>
                        <td className="num text-[#166534]">{fmtINR(s.principal)}</td>
                        <td className="num font-mono">{fmtINR(s.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Loan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Lender*</Label><Input value={f.lender} onChange={(e) => setF({ ...f, lender: e.target.value })} /></div>
            <div><Label>Principal</Label><Input type="number" value={f.principal} onChange={(e) => setF({ ...f, principal: +e.target.value })} /></div>
            <div><Label>Annual Rate %</Label><Input type="number" value={f.rate} onChange={(e) => setF({ ...f, rate: +e.target.value })} /></div>
            <div><Label>Tenure (months)</Label><Input type="number" value={f.tenure_months} onChange={(e) => setF({ ...f, tenure_months: +e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="loan-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
